/**
 * @fileoverview Product Analytics - Page View Tracking
 * @module features/analytics/page-views
 * 
 * @description
 * Track page views with rich context including scroll depth, time on page,
 * referrer information, and user engagement signals.
 * 
 * @useCases
 * - Track which pages users visit most frequently
 * - Measure time spent on content pages
 * - Identify high bounce rate pages
 * - Analyze user navigation patterns
 * - Monitor landing page performance
 * 
 * @metrics
 * - Total page views
 * - Unique page views  
 * - Average time on page
 * - Bounce rate by page
 * - Scroll depth distribution
 * - Entry/exit page analysis
 * 
 * @chartData
 * - Line chart: Page views over time
 * - Bar chart: Top pages by views
 * - Heatmap: Scroll depth by page
 * - Funnel: Page flow analysis
 * 
 * @example
 * ```typescript
 * const pageViews = new PageViews(webId, endpoint, apiKey);
 * 
 * // Track a page view
 * await pageViews.track({
 *   url: window.location.href,
 *   path: window.location.pathname,
 *   title: document.title,
 *   referrer: document.referrer
 * });
 * 
 * // Track with scroll depth
 * await pageViews.trackScrollDepth({
 *   path: '/blog/article-1',
 *   scrollDepth: 75
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { getWindow, getDocument, isBrowser, combineTeardowns, Teardown } from "../../utils/browser";
import { 
  PageView, 
  TrafficMetrics, 
  TimeSeriesData, 
  ChartDataPoint,
  DateRangeFilter,
  PaginationParams 
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Configuration for tracking a page view.
 */
export interface PageViewConfig {
  /** Full page URL */
  url: string;
  /** Page path (without domain) */
  path: string;
  /** Page title */
  title?: string;
  /** Referrer URL */
  referrer?: string;
  /** User ID (if authenticated) */
  userId?: string;
  /** Session ID */
  sessionId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Configuration for tracking scroll depth.
 */
export interface ScrollDepthConfig {
  /** Page path */
  path: string;
  /** Scroll depth percentage (0-100) */
  scrollDepth: number;
  /** Time at this depth (seconds) */
  timeAtDepth?: number;
}

/**
 * Configuration for tracking time on page.
 */
export interface TimeOnPageConfig {
  /** Page path */
  path: string;
  /** Time spent in seconds */
  duration: number;
  /** Whether user engaged (scrolled, clicked, etc.) */
  engaged?: boolean;
}

/**
 * Page view response from API.
 */
export interface PageViewResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    pageViewId: string;
    tracked: boolean;
  };
  timestamp: string;
}

/**
 * Page analytics data for charts.
 */
export interface PageAnalyticsData {
  /** Top pages */
  topPages: Array<{
    path: string;
    title: string;
    views: number;
    uniqueViews: number;
    avgTimeOnPage: number;
    bounceRate: number;
  }>;
  /** Page views time series */
  timeSeries: TimeSeriesData;
  /** Summary metrics */
  summary: {
    totalPageViews: number;
    uniquePageViews: number;
    avgTimeOnPage: number;
    avgScrollDepth: number;
    bounceRate: number;
  };
}

// ============================================================================
// PAGE VIEWS CLASS
// ============================================================================

/**
 * PageViews - Track and analyze page view events.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides comprehensive page view tracking with support for:
 * - Basic page view events
 * - Scroll depth tracking
 * - Time on page measurement
 * - Exit intent detection
 * - Page engagement scoring
 * 
 * @example
 * ```typescript
 * const pageViews = new PageViews(webId, endpoint, apiKey);
 * 
 * // Basic page view
 * await pageViews.track({
 *   url: 'https://example.com/products',
 *   path: '/products',
 *   title: 'Our Products'
 * });
 * ```
 */
export class PageViews extends FlowGridTransport {
  /**
   * Tracks a page view event.
   * 
   * @param config - Page view configuration
   * @returns Promise resolving to the API response
   * 
   * @example
   * ```typescript
   * await pageViews.track({
   *   url: window.location.href,
   *   path: window.location.pathname,
   *   title: document.title
   * });
   * ```
   */
  async track(config: PageViewConfig): Promise<PageViewResponse> {
    return await this.init("page_view", {
      type: "page_view",
      url: config.url,
      path: config.path,
      title: config.title || "",
      referrer: config.referrer || "",
      userId: config.userId,
      sessionId: config.sessionId,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks scroll depth on a page.
   * 
   * @param config - Scroll depth configuration
   * @returns Promise resolving to the API response
   * 
   * @example
   * ```typescript
   * // Track when user scrolls to 50%
   * await pageViews.trackScrollDepth({
   *   path: '/article/how-to-guide',
   *   scrollDepth: 50
   * });
   * ```
   */
  async trackScrollDepth(config: ScrollDepthConfig): Promise<PageViewResponse> {
    return await this.init("scroll_depth", {
      type: "scroll_depth",
      path: config.path,
      scrollDepth: Math.min(100, Math.max(0, config.scrollDepth)),
      timeAtDepth: config.timeAtDepth,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks time spent on a page.
   * 
   * @param config - Time on page configuration
   * @returns Promise resolving to the API response
   * 
   * @example
   * ```typescript
   * // Track on page unload
   * window.addEventListener('beforeunload', () => {
   *   pageViews.trackTimeOnPage({
   *     path: '/pricing',
   *     duration: 45,
   *     engaged: true
   *   });
   * });
   * ```
   */
  async trackTimeOnPage(config: TimeOnPageConfig): Promise<PageViewResponse> {
    return await this.init("time_on_page", {
      type: "time_on_page",
      path: config.path,
      duration: config.duration,
      engaged: config.engaged ?? false,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a page exit event.
   * 
   * @param path - Page path being exited
   * @param duration - Time spent on page
   * @param scrollDepth - Final scroll depth
   * @returns Promise resolving to the API response
   */
  async trackExit(
    path: string, 
    duration: number, 
    scrollDepth: number
  ): Promise<PageViewResponse> {
    return await this.init("page_exit", {
      type: "page_exit",
      path,
      duration,
      scrollDepth,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Auto-track page views for both classic full-page navigations and SPA
   * route changes (history.pushState / replaceState / popstate). Also wires
   * scroll-depth thresholds (25/50/75/100%) and a final time-on-page event
   * when the page is hidden or unloaded.
   *
   * @returns A teardown function that detaches every listener.
   *
   * @example
   * ```ts
   * const stop = fg.pageViews.client.autoTrack({ userId: "u_1" });
   * ```
   */
  autoTrack(options: { userId?: string; sessionId?: string } = {}): Teardown {
    if (!isBrowser()) return () => { /* no-op on the server */ };
    const win = getWindow()!;
    const doc = getDocument()!;

    let currentPath = win.location.pathname;
    let pageStart = Date.now();
    let maxDepth = 0;
    const sentDepths = new Set<number>();

    const fireView = () => {
      void this.track({
        url: win.location.href,
        path: win.location.pathname,
        title: doc.title,
        referrer: doc.referrer,
        userId: options.userId,
        sessionId: options.sessionId,
      });
    };

    const flushTimeOnPage = () => {
      const duration = Math.round((Date.now() - pageStart) / 1000);
      if (duration <= 0) return;
      void this.trackTimeOnPage({ path: currentPath, duration, engaged: maxDepth > 10 });
    };

    const onRouteChange = () => {
      if (win.location.pathname === currentPath) return;
      flushTimeOnPage();
      currentPath = win.location.pathname;
      pageStart = Date.now();
      maxDepth = 0;
      sentDepths.clear();
      fireView();
    };

    // Patch history methods so we can detect SPA route changes.
    const origPush = win.history.pushState;
    const origReplace = win.history.replaceState;
    win.history.pushState = function (...args) {
      const r = origPush.apply(this, args);
      win.dispatchEvent(new Event("flowgrid:locationchange"));
      return r;
    };
    win.history.replaceState = function (...args) {
      const r = origReplace.apply(this, args);
      win.dispatchEvent(new Event("flowgrid:locationchange"));
      return r;
    };
    const popHandler = () => onRouteChange();
    const locHandler = () => onRouteChange();
    win.addEventListener("popstate", popHandler);
    win.addEventListener("flowgrid:locationchange", locHandler);

    // Scroll-depth thresholds.
    const onScroll = () => {
      const docEl = doc.documentElement;
      const scrollTop = win.scrollY || docEl.scrollTop;
      const height = Math.max(1, docEl.scrollHeight - win.innerHeight);
      const depth = Math.min(100, Math.round((scrollTop / height) * 100));
      if (depth > maxDepth) maxDepth = depth;
      for (const t of [25, 50, 75, 100]) {
        if (depth >= t && !sentDepths.has(t)) {
          sentDepths.add(t);
          void this.trackScrollDepth({ path: currentPath, scrollDepth: t });
        }
      }
    };
    win.addEventListener("scroll", onScroll, { passive: true });

    // Flush on unload / visibility change.
    const onHide = () => { if (doc.visibilityState === "hidden") flushTimeOnPage(); };
    doc.addEventListener("visibilitychange", onHide);
    win.addEventListener("pagehide", flushTimeOnPage);

    // Initial view.
    fireView();

    return combineTeardowns(
      () => win.removeEventListener("popstate", popHandler),
      () => win.removeEventListener("flowgrid:locationchange", locHandler),
      () => win.removeEventListener("scroll", onScroll),
      () => doc.removeEventListener("visibilitychange", onHide),
      () => win.removeEventListener("pagehide", flushTimeOnPage),
      () => { win.history.pushState = origPush; win.history.replaceState = origReplace; },
    );
  }
}

export default PageViews;
